









































import { Component, Watch } from 'vue-property-decorator';
import MixinPage from '@/plugins/podolib/services/helper/mixins/page';

import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { TAcquisition, TPatient } from '@/plugins/podolib/services/entities/EntityPatients';

// Components
import Page from '@/components/Page.vue';
import PatientFile from '@/components/patients/PatientFile.vue';
import PatientSidebar from '@/components/patients/PatientSidebar.vue';
import PatientAcquisitionExportation from '@/components/patients/acquisition/exportation/PatientAcquisitionExportation.vue';
import PatientAcquisitionExportationExcel from '@/components/patients/acquisition/exportation/PatientAcquisitionExportationExcel.vue';

/**
 * # Patient page
 * Include :
 * - Patient
 * - Analysis
 */
@Component({
  components: {
    Page,
    PatientFile,
    PatientSidebar,
    PatientAcquisitionExportation,
    PatientAcquisitionExportationExcel: PatientAcquisitionExportationExcel,
  },
})
export default class PagePatient extends MixinPage {
  // Patient
  patient: TEntity<TPatient> | null = null;
  // Flag to indicate if the recovery of patient's data from the api is in progress.
  patientDataRecoveryInProgress = false;

  error: Error | null = null;

  // TODO : add type
  compareAcquisition: any = {};

  /**
   * Get patient id from route params
   */
  get patientId() {
    return this.$route.params.id;
  }

  /**
   * Get acquisition id from route params
   */
  get acquisitionId() {
    return this.$route.params.acquisitionId;
  }

  /**
   * Meta helper
   */
  get metaInfo() {
    // TODO : can be improved to display patient's name and child page
    return {
      title: this.$options.filters!.capitalize(this.$tc('commons.standards.patient', 2)),
      titleTemplate: '%s | Podosmart',
    };
  }

  // Get mini variant state of patient's sidebar
  get patientSidebarMiniVariant(): boolean {
    return this.$pdc.storage!.getUniversal('patientSidebarMiniVariant');
  }

  // Is evolution page ?
  get isPageEvolution() {
    return this.$route.fullPath === `/patient/${this.patientId}/evolution`;
  }

  // Is comparaison page ?
  get isPageComparison() {
    return this.$route.fullPath === `/patient/${this.patientId}/comparison`;
  }

  // Is analysis page ?
  get isPageAnalysis() {
    return new RegExp(`^/patient/${this.patientId}/analysis`).test(this.$route.fullPath);
  }

  // Get visibility state of patient's card
  get displayPatientFile() {
    return !this.isPageEvolution && !this.isPageComparison && !this.acquisitionId && !this.isPageAnalysis;
  }

  @Watch('$route.path', { deep: true })
  onRoutePathChanged() {
    this.updateBreadcrumbs(this.$route.params);
  }

  /**
   * Retrieve patient's data from the api
   * - fetch patient
   * - update breadcrumbs
   */
  async fetchPatient(): Promise<void> {
    // Clear error
    this.error = null;
    // Up flag
    this.patientDataRecoveryInProgress = true;
    // Update breadcrumbs
    this.updateBreadcrumbs(this.$route.params);

    try {
      // Try to retrieve patient's data from the api...
      this.patient = await this.$pdc.repository!.getRessource({
        identifier: this.$route.params.id,
        ressource: 'patients',
        params: {
          $populate: [
            {
              path: 'acquisitions',
              options: {
                sort: {
                  createdAt: -1,
                },
                populate: {
                  path: 'pathologies.reference',
                },
              },
            },
            {
              path: 'infos.pathologies.reference',
            },
          ],
        },
      });
    } catch (e) {
      this.error = e;
    } finally {
      // Down flag
      this.patientDataRecoveryInProgress = false;
      // Update breadcrumbs
      this.updateBreadcrumbs(this.$route.params);
    }
  }

  /**
   * Path patient
   * @param {TEntity<TPatient>} patient
   */
  async patchPatient(patient: TEntity<TPatient>) {
    if (this.patient?._id === patient._id)
      this.patient = await this.$pdc.repository?.getRessource({
        identifier: patient._id,
        ressource: 'patients',
        params: {
          $populate: [
            {
              path: 'acquisitions',
              options: {
                sort: {
                  createdAt: -1,
                },
                populate: {
                  path: 'pathologies.reference',
                },
              },
            },
            {
              path: 'infos.pathologies.reference',
            },
          ],
        },
      });
  }

  /**
   * Path acquisitions
   * @param {TEntity<TAcquisition>} acquisition
   */
  async onAcquisitionPatched(acquisition: TEntity<TAcquisition>) {
    if (this.patient?._id === acquisition.patient) {
      const acquisitionPatchedIndex = this.patient!.acquisitions.findIndex((x: any) => x._id === acquisition._id);

      const patchedAcquisition = await this.$pdc.repository?.getRessource({
        identifier: acquisition._id,
        ressource: 'acquisitions',
        params: {
          $populate: [
            {
              path: 'pathologies.reference',
            },
          ],
        },
      });

      this.$set(this.patient!.acquisitions, acquisitionPatchedIndex, patchedAcquisition);
    }
  }

  getCompareAcquisition(acquisition: any) {
    this.compareAcquisition = acquisition;
  }

  /**
   * Update breadcrumbs
   * @param params
   */
  updateBreadcrumbs(params: { id?: string; acquisitionId?: string }) {
    // Path buffer with first layer
    const path = [
      {
        text: this.$tc('commons.standards.patient', 2),
        to: '/patients',
        exact: true,
        disabled: false,
      },
    ];
    // Wait patient's data to be recovered
    if (this.patientDataRecoveryInProgress) {
      // Second layer
      path.push({
        text: `${this.$t('commons.standards.loading')}...`,
        to: `/patient/${params.id}`,
        exact: true,
        disabled: true,
      });
    }
    // If patient's data are available and no error occured
    else if (this.patient && !this.error) {
      // Second layer
      path.push({
        text: `${this.patient.infos.firstName} ${this.patient.infos.lastName}`,
        to: `/patient/${params.id}`,
        exact: true,
        disabled: false,
      });

      // Third layer
      if (this.acquisitionId) {
        path.push({
          text: this.acquisitionId,
          to: `/patient/${this.patientId}/result/${this.acquisitionId}`,
          exact: true,
          disabled: false,
        });
      } else if (this.isPageAnalysis) {
        path.push({
          text: this.$tc('commons.standards.acquisition') as string,
          to: `/patient/${this.patientId}/acquisition`,
          exact: true,
          disabled: false,
        });
      } else if (this.$route.fullPath === `/patient/${this.patientId}/evolution`) {
        path.push({
          text: this.$t('commons.standards.evolution') as string,
          to: `/patient/${this.patientId}/evolution`,
          exact: true,
          disabled: false,
        });
      } else if (this.$route.fullPath === `/patient/${this.patientId}/comparison`) {
        path.push({
          text: this.$t('commons.standards.comparison') as string,
          to: `/patient/${this.patientId}/comparison`,
          exact: true,
          disabled: false,
        });
      }
    }
    // Emit update event
    this.$pdc.bus!.emitDebug('app:navbar:breadcrumbs', path);
  }

  /**
   * On created
   * - fetch patient
   * - start listeners
   */
  async created() {
    // Fetch patient
    await this.fetchPatient();
    // Start listeners
    this.$pdc.repository!.socket!.session?.on('patients patched', this.patchPatient);
    this.$pdc.repository!.socket!.session?.on('acquisitions patched', this.onAcquisitionPatched);
  }

  /**
   * On mounted
   * - track view action
   * - force patient sidebar to reopen
   */
  async mounted() {
    // Analytics
    this.$pdc.analytics?.actions.trackSuccess('Patient CardViewed', {
      patientId: this.$route.params.id,
    });

    // Reopen patient sidebar when go to patient
    this.$pdc.storage!.setUniversal('patientSidebarMiniVariant', false);
  }

  /**
   * On beforeDestroy
   * - destroy listeners
   */
  beforeDestroy() {
    this.$pdc.repository!.socket!.session?.off('patients patched', this.patchPatient);
    this.$pdc.repository!.socket!.session?.off('acquisitions patched', this.onAcquisitionPatched);
  }
}
